import { useState, useEffect, useRef, useCallback } from 'react';

const INACTIVITY_LIMIT_MS = 30 * 60 * 1000; // 30 minutes
const WARNING_AT_MS = 28 * 60 * 1000;       // warn at 28 minutes
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
const STORAGE_KEY = 'empirex-last-activity';

/**
 * useInactivityTimeout
 * Tracks user activity and triggers warning at 28m, logout at 30m.
 * @param {Function} onLogout - called when inactivity limit reached
 * @param {boolean} enabled - only run when user is authenticated
 */
export function useInactivityTimeout({ onLogout, enabled = true }) {
  const [showWarning, setShowWarning] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);
  const warningTimerRef = useRef(null);
  const logoutTimerRef = useRef(null);
  const mountedRef = useRef(true);

  const clearTimers = useCallback(() => {
    clearTimeout(warningTimerRef?.current);
    clearTimeout(logoutTimerRef?.current);
  }, []);

  const resetTimers = useCallback(() => {
    if (!mountedRef?.current || !enabled) return;
    clearTimers();
    localStorage.setItem(STORAGE_KEY, Date.now()?.toString());
    setShowWarning(false);

    warningTimerRef.current = setTimeout(() => {
      if (mountedRef?.current) setShowWarning(true);
    }, WARNING_AT_MS);

    logoutTimerRef.current = setTimeout(() => {
      if (mountedRef?.current) {
        setShowWarning(false);
        onLogout?.('inactivity');
      }
    }, INACTIVITY_LIMIT_MS);
  }, [enabled, clearTimers, onLogout]);

  const stayLoggedIn = useCallback(() => {
    resetTimers();
  }, [resetTimers]);

  // Check on mount if session already expired
  useEffect(() => {
    if (!enabled) return;
    const lastActivity = parseInt(localStorage.getItem(STORAGE_KEY) || '0', 10);
    if (lastActivity && Date.now() - lastActivity > INACTIVITY_LIMIT_MS) {
      setSessionExpired(true);
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;
    mountedRef.current = true;

    const handleActivity = () => resetTimers();
    ACTIVITY_EVENTS?.forEach(evt => window.addEventListener(evt, handleActivity, { passive: true }));
    resetTimers();

    return () => {
      mountedRef.current = false;
      clearTimers();
      ACTIVITY_EVENTS?.forEach(evt => window.removeEventListener(evt, handleActivity));
    };
  }, [enabled, resetTimers, clearTimers]);

  return { showWarning, sessionExpired, stayLoggedIn, clearSessionExpired: () => setSessionExpired(false) };
}

export default useInactivityTimeout;
